/**
 * Shared layout engine for Milestone Summary Timeline
 * Pure function — used by both Preview and Export for pixel-perfect consistency.
 */

import { differenceInDays } from 'date-fns';

// ============================================================================
// RESOLVE: re-derive summary milestone dates from CURRENT items/milestones.
// Avoids stale snapshot dates stored in summaryMilestones. Also dedupes
// duplicate entries that point to the same (source, role).
// ============================================================================

interface ResolveItemLike {
  id: string;
  startDate: string;
  endDate: string;
}
interface ResolveMilestoneLike {
  id?: string;
  start_date?: string;
  end_date?: string;
}
interface ResolveSummaryEntry {
  id: string;
  date: string;
  name: string;
  note?: string;
  color: string;
  type?: 'item' | 'milestone';
  sourceItemId?: string;
  sourceItemName?: string;
  [key: string]: any;
}

const ROLE_NOTE_PLACEHOLDERS = new Set(['Bắt đầu', 'Kết thúc', 'Start', 'End']);

function pickCustomNote(note?: string): string {
  if (!note) return '';
  const t = note.trim();
  if (!t) return '';
  if (ROLE_NOTE_PLACEHOLDERS.has(t)) return '';
  return t;
}

export function resolveSummaryMilestoneDates<T extends ResolveSummaryEntry>(
  entries: T[],
  items: ResolveItemLike[],
  milestonesByItem?: Record<string, ResolveMilestoneLike[]>,
): T[] {
  if (!entries || entries.length === 0) return entries ?? [];

  const itemById = new Map<string, ResolveItemLike>();
  for (const it of items || []) itemById.set(it.id, it);

  const milestoneById = new Map<string, ResolveMilestoneLike>();
  if (milestonesByItem) {
    for (const list of Object.values(milestonesByItem)) {
      for (const m of list || []) { if (m && m.id) milestoneById.set(m.id, m); }
    }
  }

  type Role = 'start' | 'end';
  interface Resolved {
    entry: T;
    sourceKey: string;
    role: Role;
    prefixed: boolean;
  }

  const resolvedList: Resolved[] = [];

  for (const entry of entries) {
    let date = entry.date;
    let sourceKey = `raw:${entry.id}`;
    let role: Role = 'start';
    let prefixed = true;

    const id = entry.id || '';
    if (id.startsWith('item-start-')) {
      const itemId = id.slice('item-start-'.length);
      const it = itemById.get(itemId);
      if (it) date = it.startDate;
      sourceKey = `item:${itemId}`;
      role = 'start';
    } else if (id.startsWith('item-end-')) {
      const itemId = id.slice('item-end-'.length);
      const it = itemById.get(itemId);
      if (it) date = it.endDate;
      sourceKey = `item:${itemId}`;
      role = 'end';
    } else if (id.startsWith('milestone-start-')) {
      const mid = id.slice('milestone-start-'.length);
      const m = milestoneById.get(mid);
      if (m?.start_date) date = m.start_date;
      sourceKey = `milestone:${mid}`;
      role = 'start';
    } else if (id.startsWith('milestone-end-')) {
      const mid = id.slice('milestone-end-'.length);
      const m = milestoneById.get(mid);
      if (m?.end_date) date = m.end_date;
      sourceKey = `milestone:${mid}`;
      role = 'end';
    } else if (entry.type === 'milestone') {
      // legacy bare uuid → treat as start
      const m = milestoneById.get(id);
      if (m?.start_date) date = m.start_date;
      sourceKey = `milestone:${id}`;
      role = 'start';
      prefixed = false;
    }

    resolvedList.push({
      entry: { ...entry, date },
      sourceKey,
      role,
      prefixed,
    });
  }

  // Dedupe by (sourceKey, role). Prefer prefixed entry; preserve any custom note.
  const byKey = new Map<string, Resolved>();
  for (const r of resolvedList) {
    const k = `${r.sourceKey}|${r.role}`;
    const existing = byKey.get(k);
    if (!existing) {
      byKey.set(k, r);
      continue;
    }
    const chosen: Resolved = r.prefixed && !existing.prefixed ? r : existing;
    const noteExisting = pickCustomNote(existing.entry.note);
    const noteNew = pickCustomNote(r.entry.note);
    const finalNote = noteExisting || noteNew || chosen.entry.note || '';
    byKey.set(k, {
      ...chosen,
      entry: { ...chosen.entry, note: finalNote },
    });
  }

  return Array.from(byKey.values()).map((v) => v.entry);
}

export interface LayoutMilestone {
  id: string;
  date: string;
  name: string;
  note?: string;
  color: string;
  sourceItemName?: string;
  type?: 'item' | 'milestone';
  // Manual offsets (nullable = auto position)
  dateOffsetX?: number;
  dateOffsetY?: number;
  noteOffsetX?: number;
  noteOffsetY?: number;
  pinned?: boolean;
}

export function getMilestoneDisplayNote(milestone: LayoutMilestone): string {
  const rawNote = typeof milestone.note === 'string' ? milestone.note.trim() : '';

  // legacy: milestone entry cũ từng lưu note = tên item cha → coi như KHÔNG phải custom note
  const sourceItemName = typeof milestone.sourceItemName === 'string' ? milestone.sourceItemName.trim() : '';
  const isLegacyParentNote =
    milestone.type === 'milestone' &&
    sourceItemName !== '' &&
    rawNote === sourceItemName;

  const hasCustomNote =
    rawNote !== '' &&
    rawNote !== milestone.name &&
    !isLegacyParentNote &&
    !['Bắt đầu', 'Kết thúc', 'Start', 'End'].includes(rawNote);

  if (hasCustomNote) {
    return rawNote;
  }

  // Milestone type: always show milestone's own name (do NOT fall back to parent item name)
  if (milestone.type === 'milestone') {
    return milestone.name;
  }

  // Item Start/End: fall back to parent item name for context
  if (typeof milestone.sourceItemName === 'string' && milestone.sourceItemName.trim() !== '') {
    return milestone.sourceItemName;
  }

  return milestone.name;
}

// ============================================================================
// STEPPER LAYOUT (PR1) — fixed-width label slots + sqrt(gapDays) connectors.
// Replaces time-proportional positioning. Preview & export will share this.
// ============================================================================

export interface StepperLayoutInput {
  milestones: LayoutMilestone[];   // expected sorted by date (function also sorts defensively)
  containerWidthPx: number;        // W
  slotW?: number;                  // default 110
  minConn?: number;                // default 24
  autoFitMode?: 'scroll' | 'shrink'; // 'scroll' (preview) | 'shrink' (export)
}

export interface StepperNode {
  id: string;
  centerPx: number;
  slotLeftPx: number;
  slotW: number;
  daysToNext: number | null;
}

export interface StepperLayout {
  nodes: StepperNode[];
  totalWidthPx: number;
  overflow: boolean;
  slotW: number;
}

export function computeMilestoneStepperLayout(input: StepperLayoutInput): StepperLayout {
  const {
    containerWidthPx: W,
    slotW: slotWIn = 110,
    minConn: minConnIn = 24,
    autoFitMode = 'scroll',
  } = input;

  // Defensive sort by date (callers usually pre-sort).
  const sorted = [...input.milestones].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const N = sorted.length;

  if (N === 0) {
    return { nodes: [], totalWidthPx: 0, overflow: false, slotW: slotWIn };
  }

  if (N === 1) {
    const centerPx = Math.max(slotWIn / 2, W / 2);
    return {
      nodes: [{
        id: sorted[0].id,
        centerPx,
        slotLeftPx: centerPx - slotWIn / 2,
        slotW: slotWIn,
        daysToNext: null,
      }],
      totalWidthPx: Math.max(W, slotWIn),
      overflow: slotWIn > W,
      slotW: slotWIn,
    };
  }

  // gapDays[i] = days between m[i] and m[i+1], min 1
  const gapDays: number[] = [];
  for (let i = 0; i < N - 1; i++) {
    const g = differenceInDays(
      new Date(sorted[i + 1].date).getTime(),
      new Date(sorted[i].date).getTime()
    );
    gapDays.push(Math.max(1, g));
  }

  // Knob: 0.5 = sqrt (even), 1 = linear (strong contrast between short & long gaps)
  const DURATION_WEIGHT_EXPONENT = 1;
  const weight = gapDays.map(g => Math.pow(g, DURATION_WEIGHT_EXPONENT));
  const Wsum = weight.reduce((a, b) => a + b, 0) || 1;

  const needed = N * slotWIn + (N - 1) * minConnIn;
  const overflow = needed > W;

  // autoFit
  let effSlotW = slotWIn;
  let effMinConn = minConnIn;
  let effW = W;

  if (overflow) {
    if (autoFitMode === 'shrink') {
      // Shrink slotW first (min 70), then minConn (min 16). Keep effW = W.
      const candidateSlotW = (W - (N - 1) * minConnIn) / N;
      effSlotW = Math.max(70, Math.min(slotWIn, candidateSlotW));
      if (N * effSlotW + (N - 1) * minConnIn > W) {
        effMinConn = 16;
        const candidateSlotW2 = (W - (N - 1) * effMinConn) / N;
        effSlotW = Math.max(70, Math.min(slotWIn, candidateSlotW2));
      }
      effW = W;
    } else {
      // 'scroll' (preview): expand canvas to needed; let parent scroll horizontally.
      effSlotW = slotWIn;
      effMinConn = minConnIn;
      effW = needed;
    }
  }

  const availConn = Math.max(0, effW - N * effSlotW - (N - 1) * effMinConn);
  const connector = weight.map(w => effMinConn + availConn * (w / Wsum));

  const nodes: StepperNode[] = [];
  let center = effSlotW / 2;
  for (let i = 0; i < N; i++) {
    if (i > 0) {
      center = center + effSlotW / 2 + connector[i - 1] + effSlotW / 2;
    }
    nodes.push({
      id: sorted[i].id,
      centerPx: center,
      slotLeftPx: center - effSlotW / 2,
      slotW: effSlotW,
      daysToNext: i < N - 1 ? gapDays[i] : null,
    });
  }

  const totalWidthPx = nodes[N - 1].centerPx + effSlotW / 2;

  // ---- DEV self-check (no-op in production via console.assert) ----
  if (typeof process !== 'undefined' && (process as any)?.env?.NODE_ENV !== 'production') {
    const sumConn = connector.reduce((a, b) => a + b, 0);
    const widthCheck = N * effSlotW + sumConn;
    console.assert(
      Math.abs(widthCheck - effW) < 1,
      `[stepper] width mismatch: ${widthCheck} vs ${effW}`
    );
    const wsumNorm = weight.reduce((a, b) => a + b / Wsum, 0);
    console.assert(Math.abs(wsumNorm - 1) < 1e-6, '[stepper] weight share != 1');
  }

  return { nodes, totalWidthPx, overflow, slotW: effSlotW };
}
